import { yupResolver } from '@hookform/resolvers/yup';
import { BlogFont, BlogPostCustomization, CustomPageType, IOperationResultNoValue, TextAttributes } from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useUpdateBlogStyleMutation';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { LabeledInput } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { blogPostSchema } from './schemas';

const FONT_VALUES: BlogFont = {
	fontName: '',
	fontUri: '',
};
const TEXT_ATTRIBUTES: TextAttributes = {
	font: { ...FONT_VALUES },
	color: '',
	backgroundColor: '',
};
const DEFAULT_VALUES: BlogPostCustomization = {
	blogTitle: {
		...TEXT_ATTRIBUTES,
	},
	blogHeadlines: {
		...TEXT_ATTRIBUTES,
	},
	blogBody: {
		...TEXT_ATTRIBUTES,
	},
	showMoreButton: {
		...TEXT_ATTRIBUTES,
		hoverColor: '',
	},
	type: CustomPageType.BlogPost,
};

interface PostDesignFormProps {
	customContent: BlogPostCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

export const PostDesignForm: FC<PostDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();
	const { accountId, impersonationContext } = useAccountContext();

	const {
		register,
		setValue,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
	} = useForm<BlogPostCustomization>({
		defaultValues: DEFAULT_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(blogPostSchema),
		values: customContent,
	});
	const blogTitleFont = watch('blogTitle.font', FONT_VALUES);
	const blogHeadlinesFont = watch('blogHeadlines.font', FONT_VALUES);
	const blogBodyFont = watch('blogBody.font', FONT_VALUES);
	const buttonFont = watch('showMoreButton.font', FONT_VALUES);

	const updateBlogPostMutation = useUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onValid: SubmitHandler<BlogPostCustomization> = (data, event) => {
		event.preventDefault();
		event.stopPropagation();

		updateBlogPostMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);

	return (
		<form onSubmit={handleSubmit(onValid)}>
			<Column>
				<Row className={css(styleSheet.titleRow)}>
					<h1 className={css(styleSheet.titleText)}>Customize Blog Post</h1>
					<Column className={css(styleSheet.submitColumn)}>
						<input
							className={css(bs.ctaButton)}
							type='submit'
							value='Save & Update'
							disabled={updateBlogPostMutation.isPending || !!Object.keys(errors).length}
						/>
					</Column>
				</Row>
				<Row className={css(styleSheet.gap24)}>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Background</h2>
						<LabeledInput
							error={errors?.blogBody?.backgroundColor}
							label='Page Background Color'
							{...register('blogBody.backgroundColor')}
						/>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Title</h2>
						<FontSelect
							initialValue={customContent?.blogTitle?.font}
							onChange={value => setValue('blogTitle.font', value)}
							errors={errors?.blogTitle?.font}
							value={blogTitleFont}
						/>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Headlines</h2>
						<FontSelect
							initialValue={customContent?.blogHeadlines?.font}
							onChange={value => setValue('blogHeadlines.font', value)}
							errors={errors?.blogHeadlines?.font}
							value={blogHeadlinesFont}
						/>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Body</h2>
						<FontSelect
							initialValue={customContent?.blogBody?.font}
							onChange={value => setValue('blogBody.font', value)}
							errors={errors?.blogBody?.font}
							value={blogBodyFont}
						/>
					</Column>
					<Column className={css(bs.flex1, styleSheet.gap24)}>
						<h2 className={css(styleSheet.subtitleText, bs.flex1)}>Show More Button</h2>
						<FontSelect
							initialValue={customContent?.showMoreButton?.font}
							onChange={value => setValue('showMoreButton.font', value)}
							errors={errors?.showMoreButton?.font}
							value={buttonFont}
						/>
						<LabeledInput
							error={errors?.showMoreButton?.backgroundColor}
							label='Color'
							{...register('showMoreButton.backgroundColor')}
						/>
						<LabeledInput
							error={errors?.showMoreButton?.color}
							label='Text Color'
							{...register('showMoreButton.color')}
						/>
						<LabeledInput
							error={errors?.showMoreButton?.hoverColor}
							label='Hover Color'
							{...register('showMoreButton.hoverColor')}
						/>
					</Column>
				</Row>
			</Column>
		</form>
	);
};
